import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IConfirmation } from 'src/redux/confirmation/types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { DataService } from 'src/services/data-service';
import { IContact, IContactListing, IContactDetails } from '.';
import Loader from 'src/components/Loader';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UpdateContact from './update-contact';
import UpdatePassword from './update-password';

const ContactListing: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState<Record<string, IContactListing>>();
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openUpdateContact, setOpenUpdateContact] = useState<boolean>(false);
  const [openUpdatePassword, setOpenUpdatePassword] = useState<boolean>(false);
  const [selectedContact, setSelectedContact] = useState<IContactDetails | null>(null);

  const activate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to activate this contact?',
      onSubmit: async () => {
        const result = await DataService.post('api/Contact/activate?id=' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'Contact activated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to activate contact', type: 'error' }));
        }
      }
    };

    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const deactivate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to deactivate this contact?',
      onSubmit: async () => {
        const result = await DataService.post(
          'api/Contact/deactivate?id=' + id,
          {}
        );
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'Contact deactivated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to deactivate contact', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const editContact = useCallback(async (id: string) => {
    try {
      const response = await DataService.get('api/Contact/get/' + id);
      if (response.ok) {
        const res = await response.json();
        const contact: IContactDetails = res.data;
        setSelectedContact(contact);
        setOpenUpdateContact(true);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch contact details', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading contact details', type: 'error' }));
    }
  }, [dispatch]);

  const updatePassword = useCallback(async (id: string) => {
    try {
      const response = await DataService.get('api/Contact/get/' + id);
      if (response.ok) {
        const res = await response.json();
        const contact: IContactDetails = res.data;
        setSelectedContact(contact);
        setOpenUpdatePassword(true);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch contact details', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading contact details', type: 'error' }));
    }
  }, [dispatch]);

  const viewDetails = useCallback((id: string) => {
    navigate(`/contacts/${id}`);
  }, [navigate]);

  const headers = React.useMemo((): EnhancedTableColumn[] => [
    { id: 'fullName', label: 'Full Name', type: EnhancedTableColumnType.TEXT },
    { id: 'email', label: 'Email', type: EnhancedTableColumnType.TEXT },
    {
      id: 'mobileNumber',
      label: 'Mobile Number',
      type: EnhancedTableColumnType.PHONE_NUMBER
    },
    { id: 'dateOfBirth', label: 'Date of Birth', type: EnhancedTableColumnType.DATE },
    {
      id: 'status',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: {
        Active: { backgroundColor: 'green', color: 'white' },
        Inactive: { backgroundColor: 'red', color: 'white' }
      }
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: viewDetails,
          icon: <VisibilityIcon fontSize="small" />,
          label: 'View Details'
        },
        {
          onClick: editContact,
          icon: <EditIcon fontSize="small" />,
          label: 'Edit Contact'
        },
        {
          onClick: updatePassword,
          icon: <LockIcon fontSize="small" />,
          label: 'Update Password'
        },
        {
          onClick: activate,
          icon: <CheckIcon fontSize="small" />,
          label: 'Activate Contact',
          hidden: (r) => r.active === true
        },
        {
          onClick: deactivate,
          icon: <CancelIcon fontSize="small" />,
          label: 'Deactivate Contact',
          hidden: (r) => r.active === false
        }
      ]
    }
  ], [activate, deactivate, editContact, updatePassword, viewDetails]);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DataService.get('api/Contact/all');
      if (response.ok) {
        const res = await response.json();
        const list: IContact[] = res.data;
        const result: Record<string, IContactListing> = {};

        list.forEach((contact) => {
          result[contact.id] = {
            id: contact.id,
            fullName: contact.fullName,
            email: contact.email,
            mobileNumber: contact.mobileNumber,
            dateOfBirth: contact.dateOfBirth,
            active: contact.active,
            status: contact.active ? 'Active' : 'Inactive'
          };
        });

        setData(result);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch contacts', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading contacts', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts, refreshKey]);

  const handleUpdateSuccess = useCallback(() => {
    setOpenUpdateContact(false);
    setSelectedContact(null);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({
      message: 'Contact updated successfully!',
      type: 'success'
    }));
  }, [dispatch]);

  const handlePasswordUpdateSuccess = useCallback(() => {
    setOpenUpdatePassword(false);
    setSelectedContact(null);
    dispatch(showAlert({
      message: 'Password updated successfully!',
      type: 'success'
    }));
  }, [dispatch]);

  const handleClose = useCallback(() => {
    setOpenUpdateContact(false);
    setOpenUpdatePassword(false);
    setSelectedContact(null);
  }, []);

  const actions: ITableMenuAction[] = [];

  if (!data || loading) {
    return (
      <Box>
        <Helmet>
          <title>Contact List</title>
        </Helmet>
        <MainPageTitle title={'Contacts List'} />
        <Loader />
      </Box>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Contact List</title>
      </Helmet>
      <MainPageTitle title={'Contacts List'} />

      <EnhancedTable
        title="Contacts"
        columns={headers}
        data={data}
        actions={actions}
      />

      {openUpdateContact && selectedContact && (
        <UpdateContact
          onSuccess={handleUpdateSuccess}
          onClose={handleClose}
          contact={selectedContact}
        />
      )}

      {openUpdatePassword && selectedContact && (
        <UpdatePassword
          onSuccess={handlePasswordUpdateSuccess}
          onClose={handleClose}
          contactId={selectedContact.id}
        />
      )}
    </div>
  );
};

export default ContactListing;
